import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CreditCardIcon,
  EyeIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/ui/date-picker';
import BankCombobox from '../components/BankCombobox';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { Tip } from '../components/ui/tip';
import { disconnectAppSocket, getAppSocket } from '../lib/wesSocket';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog';
import { openPdf } from '../utils/pdfHelpers';
import { hoyLocal } from '../utils/fechaLocal';
import ClientCombobox from '../components/ClientCombobox';
import ProviderCombobox from '../components/ProviderCombobox';

interface CurrentAccount {
  id: string;
  clientId?: string;
  entityId: string;
  entityType: 'client' | 'provider';
  creditLimit: number;
  currentBalance: number;
  linkedEntityId?: string | null;
  linkedEntityType?: 'client' | 'provider' | null;
  linkedName?: string | null;
  linkedBalance?: number | null;
  status: string;
  notes: string;
  // Campos para clientes
  firstName?: string;
  lastName?: string;
  // Campos para proveedores
  providerName?: string;
  // Campos comunes
  companyName?: string;
  phone?: string;
  email?: string;
  displayName?: string;
  createdAt: string;
  updatedAt: string;
  lastMovementAt?: string | null;
  lastChargeAt?: string | null;
  lastPaymentAt?: string | null;
  debtAgeDays?: number | null;
  daysSinceLastMovement?: number | null;
  totalCharges?: number;
  totalPayments?: number;
}

interface Movement {
  id: string;
  accountId: string;
  type: 'charge' | 'payment' | 'credit' | 'debit' | 'adjustment';
  amount: number;
  description: string;
  reference?: string;
  invoiceId?: string;
  budgetId?: string;
  paymentMethod?: string;
  previousBalance: number;
  newBalance: number;
  createdBy: string;
  createdByName?: string;
  createdByLastName?: string;
  createdAt: string;
}

interface PaymentForm {
  amount: number;
  paymentMethod: string;
  description?: string;
  bank?: string;
  checkDate?: string;
  checkNumber?: string;
  checkBank?: string;
}

interface CreditLimitForm {
  creditLimit: number;
}

interface Stats {
  totalAccounts: number;
  accountsWithDebt: number;
  totalDebt: number;
  overLimitAccounts: number;
  monthlyMovements: number;
}

const CurrentAccounts: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlSearchQuery = (searchParams.get('q') || '').trim();
  const [accounts, setAccounts] = useState<CurrentAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<CurrentAccount | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  // Saldo inicial (migración sistema anterior)
  const [showOpeningModal, setShowOpeningModal] = useState(false);
  const [openingEntities, setOpeningEntities] = useState<any[]>([]);
  const [openingSelected, setOpeningSelected] = useState<any | null>(null);
  const [openingAmount, setOpeningAmount] = useState('');
  const [openingDate, setOpeningDate] = useState('');
  const [openingNote, setOpeningNote] = useState('');
  const [openingSaving, setOpeningSaving] = useState(false);
  const [showCreditLimitModal, setShowCreditLimitModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'clients' | 'providers'>('clients');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<Array<{ code: string; name: string; enabled?: boolean }>>([]);
  useEffect(() => {
    axios.get('/api/settings/payment-methods')
      .then(r => setPaymentMethodOptions((Array.isArray(r.data) ? r.data : []).filter((m: any) => m.enabled !== false && m.enabled !== 0)))
      .catch(() => {});
  }, []);
  const [searchTerm, setSearchTerm] = useState(urlSearchQuery);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    hasDebt: false,
    overdueDebt: false,
    noMovementDays: '',
    overLimit: false,
    creditBalance: false,
    status: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const filter = 'all';
  const refreshTimeoutRef = useRef<number | null>(null);
  const fetchAccountsRef = useRef<null | ((options?: { silent?: boolean }) => void)>(null);
  const fetchStatsRef = useRef<null | (() => void)>(null);

  const { register: registerPayment, handleSubmit: handleSubmitPayment, reset: resetPayment, control: paymentControl, formState: { errors: paymentErrors } } = useForm<PaymentForm>({ shouldUnregister: true });
  const { register: registerCredit, handleSubmit: handleSubmitCredit, reset: resetCredit, formState: { errors: creditErrors } } = useForm<CreditLimitForm>();

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedPaymentMethod('');
    setSelectedAccount(null);
    setAccountSearchTerm('');
    resetPayment();
  };

  const closeCreditLimitModal = () => {
    setShowCreditLimitModal(false);
    resetCredit();
  };

  const handleQuickRegisterPayment = useCallback(() => {
    navigate('/current-accounts/pay');
  }, [navigate]);

  useEffect(() => {
    fetchAccounts();
    fetchStats();
  }, [filters, activeTab]);

  useEffect(() => {
    const socket = getAppSocket();
    const scheduleSilentRefresh = (payload?: any) => {
      const path = payload?.path ? String(payload.path) : '';
      if (path && !(
        path.startsWith('/api/current-accounts') ||
        path.startsWith('/api/remitos') ||
        path.startsWith('/api/checks')
      )) {
        return;
      }

      if (refreshTimeoutRef.current) window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = window.setTimeout(() => {
        fetchAccountsRef.current?.({ silent: true });
        fetchStatsRef.current?.();
      }, 400);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('cash:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('cash:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
      disconnectAppSocket();
    };
  }, []);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.key === 'n' || event.key === 'N') && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const target = event.target as HTMLElement | null;
        if (target) {
          const tag = target.tagName.toLowerCase();
          if (tag === 'input' || tag === 'textarea' || target.getAttribute('contenteditable') === 'true') {
            return;
          }
        }
        event.preventDefault();
        handleQuickRegisterPayment();
      }
    };

    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [handleQuickRegisterPayment]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    setSearchTerm(urlSearchQuery);
    setCurrentPage(1);
  }, [urlSearchQuery]);

  const fetchAccounts = async (options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent);
    try {
      if (!silent) setLoading(true);
      const params = new URLSearchParams();
      
      // Filtrar por tipo de entidad según la pestaña activa
      params.append('entityType', activeTab === 'clients' ? 'client' : 'provider');
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.hasDebt) params.append('hasDebt', 'true');
      if (filters.overdueDebt) params.append('overdueDebt', 'true');
      if (filters.overLimit) params.append('overLimit', 'true');
      if (filters.creditBalance) params.append('creditBalance', 'true');
      if (filters.noMovementDays) params.append('noMovementDays', filters.noMovementDays);
      
      const response = await axios.get(`/api/current-accounts?${params.toString()}`);
      setAccounts(response.data.accounts);
    } catch (error) {
      if (!silent) toast.error('Error al cargar cuentas corrientes');
    } finally {
      if (!silent) setLoading(false);
    }
  };
  fetchAccountsRef.current = fetchAccounts;

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/current-accounts/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };
  fetchStatsRef.current = fetchStats;

  const handleAddPayment = async (data: PaymentForm) => {
    if (!selectedAccount) {
      toast.error('Selecciona una cuenta para registrar el pago.');
      return;
    }

    try {
      const accountId = selectedAccount.id;
      await axios.post(`/api/current-accounts/${accountId}/payment`, data);

      // No abrimos automáticamente el recibo PDF
      // El usuario podrá imprimirlo desde la página de detalles de la cuenta

      toast.success('Pago registrado exitosamente');
      closePaymentModal();
      fetchAccounts();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al registrar pago');
    }
  };

  const handleUpdateCreditLimit = async (data: CreditLimitForm) => {
    if (!selectedAccount) return;
    
    try {
      await axios.put(`/api/current-accounts/${selectedAccount.id}/credit-limit`, data);
      toast.success('Límite de crédito actualizado exitosamente');
      closeCreditLimitModal();
      fetchAccounts();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al actualizar límite');
    }
  };

  const openAccountDetails = (account: CurrentAccount) => {
    // Navegar a la página de detalles en lugar de abrir un modal
    navigate(`/current-accounts/${account.id}`);
  };

  const getBalanceColor = (balance: number, creditLimit: number) => {
    if (balance === 0) return 'text-gray-600 dark:text-gray-400';
    if (balance > 0 && creditLimit > 0 && balance > creditLimit) return 'text-red-600 dark:text-red-400';
    if (balance > 0) return 'text-orange-600 dark:text-orange-400';
    return 'text-green-600 dark:text-green-400';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  };

  const formatDate = (value?: string | null) => {
    if (!value) return 'Sin datos';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getAgeLabel = (days?: number | null) => {
    if (days === null || days === undefined) return 'Sin datos';
    if (days <= 0) return 'Hoy';
    if (days === 1) return '1 día';
    return `${days} días`;
  };

  const getAgeBadgeClass = (days?: number | null) => {
    if (days === null || days === undefined) {
      return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';
    }
    if (days <= 7) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200';
    if (days <= 30) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200';
    return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200';
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Activa';
      case 'suspended':
        return 'Suspendida por deuda';
      case 'follow_up':
        return 'En seguimiento';
      case 'blocked':
        return 'Bloqueada';
      case 'closed':
        return 'Cerrada';
      default:
        return 'Activa';
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-500';
      case 'suspended':
        return 'bg-amber-500';
      case 'follow_up':
        return 'bg-blue-500';
      case 'blocked':
        return 'bg-red-500';
      case 'closed':
        return 'bg-slate-500';
      default:
        return 'bg-slate-400';
    }
  };

  const generatePDF = async () => {
    try {
      // Generar PDF usando POST con autenticacion estandar
      const response = await fetch('/api/current-accounts/generate-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          entityType: activeTab,
          filter: filter
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const filename = `Cuenta-${activeTab}-${new Date().toISOString().split('T')[0]}.pdf`;
        const result = openPdf({ blob, filename });
        toast.success(result === 'downloaded' ? 'PDF descargado' : 'PDF abriendo en nueva pestaña');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error en la respuesta del servidor');
      }
    } catch (error: any) {
      console.error('Error generando PDF:', error);
      toast.error(`Error al generar el PDF: ${error.message}`);
    }
  };

  const generateAccountPDF = async (account: CurrentAccount) => {
    try {
      toast.info('Generando PDF de movimientos...');
      const response = await fetch(`/api/current-accounts/${account.id}/movements-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const name = getDisplayName(account).replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ ]/g, '').trim().replace(/\s+/g, '_');
        const filename = `Movimientos-${name}-${hoyLocal()}.pdf`;
        const result = openPdf({ blob, filename });
        toast.success(result === 'downloaded' ? 'PDF descargado' : 'PDF abriendo en nueva pestaña');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error en la respuesta del servidor');
      }
    } catch (error: any) {
      console.error('Error generando PDF de movimientos:', error);
      toast.error(`Error al generar el PDF: ${error.message}`);
    }
  };

  // Saldo inicial: abrir el diálogo cargando todos los clientes o proveedores según la pestaña
  const openOpeningModal = async () => {
    setOpeningSelected(null);
    setOpeningAmount('');
    setOpeningDate('');
    setOpeningNote('');
    setShowOpeningModal(true);
    try {
      if (activeTab === 'clients') {
        const r = await axios.get('/api/clients?limit=10000');
        setOpeningEntities(r.data.clients || []);
      } else {
        const r = await axios.get('/api/providers?limit=10000');
        setOpeningEntities(r.data.providers || []);
      }
    } catch {
      setOpeningEntities([]);
      toast.error('No se pudo cargar la lista');
    }
  };

  const saveOpeningBalance = async () => {
    if (!openingSelected?.id) { toast.error('Elegí un cliente o proveedor'); return; }
    const amount = Number(openingAmount);
    if (Number.isNaN(amount) || amount < 0) { toast.error('Monto inválido'); return; }
    try {
      setOpeningSaving(true);
      await axios.post('/api/current-accounts/opening-balance', {
        entityType: activeTab === 'clients' ? 'client' : 'provider',
        entityId: openingSelected.id,
        amount,
        date: openingDate || undefined,
        note: openingNote.trim() || undefined
      });
      toast.success('Saldo inicial cargado');
      setShowOpeningModal(false);
      fetchAccounts({ silent: true });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo cargar el saldo inicial');
    } finally {
      setOpeningSaving(false);
    }
  };

  const getDisplayName = (account: CurrentAccount) => {
    if (account.entityType === 'provider') {
      return account.companyName || account.providerName || 'Proveedor sin nombre';
    } else {
      return account.companyName || `${account.firstName || ''} ${account.lastName || ''}`.trim() || 'Cliente sin nombre';
    }
  };

  const filteredAccounts = accounts.filter((account) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const name = getDisplayName(account).toLowerCase();
    const email = account.email?.toLowerCase() || '';
    const phone = account.phone?.toLowerCase() || '';
    return name.includes(term) || email.includes(term) || phone.includes(term);
  });

  const totalPages = Math.max(1, Math.ceil(filteredAccounts.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAccounts = filteredAccounts.slice(startIndex, endIndex);
  const totalResults = filteredAccounts.length;
  const startResult = totalResults === 0 ? 0 : startIndex + 1;

  const goToFirstPage = () => setCurrentPage(1);
  const goToPreviousPage = () => setCurrentPage((prev) => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  const goToLastPage = () => setCurrentPage(totalPages);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const filteredAccountOptions = accounts.filter((account) =>
    getDisplayName(account).toLowerCase().includes(accountSearchTerm.toLowerCase())
  );

  const statCards = stats
    ? [
        {
          label: 'Con deuda',
          value: stats.accountsWithDebt.toString(),
          icon: ExclamationTriangleIcon,
          accent: 'text-orange-600 dark:text-orange-300',
          bg: 'bg-orange-50 dark:bg-orange-950/20'
        },
        {
          label: 'Deuda total',
          value: formatCurrency(stats.totalDebt),
          icon: BanknotesIcon,
          accent: 'text-rose-600 dark:text-rose-300',
          bg: 'bg-rose-50 dark:bg-rose-950/20'
        },
        {
          label: 'Sobre límite',
          value: stats.overLimitAccounts.toString(),
          icon: XMarkIcon,
          accent: 'text-amber-600 dark:text-amber-300',
          bg: 'bg-amber-50 dark:bg-amber-950/20'
        }
      ]
    : [];

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-muted/30 border-t-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-10 p-4 sm:p-6">
      <section className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h2 className="text-2xl font-bold tracking-tight">Cuentas Corrientes</h2>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button
              variant="outline"
              onClick={openOpeningModal}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-border px-4 text-sm font-medium"
            >
              <BanknotesIcon className="h-4 w-4" />
              Saldo inicial
            </Button>
            <Button
              variant="outline"
              onClick={generatePDF}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-border px-4 text-sm font-medium"
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              Generar PDF
            </Button>
            <Button
              onClick={handleQuickRegisterPayment}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-black px-4 text-sm font-medium text-white shadow-sm transition-colors hover:bg-black/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              <BanknotesIcon className="h-4 w-4" />
              Registrar pago
              <span className="ml-2 hidden md:inline">
                <span className="flex items-center gap-1 text-xs text-white/80 dark:text-gray-900/70">
                  <kbd className="rounded-sm border border-white/30 px-1 text-[10px] font-semibold dark:border-gray-900/30">
                    N
                  </kbd>
                </span>
              </span>
            </Button>
          </div>
        </div>

        {/* Selector de tipo de cuenta */}
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'clients' ? 'default' : 'outline'}
            onClick={() => setActiveTab('clients')}
            className="h-9"
          >
            Clientes
          </Button>
          <Button
            variant={activeTab === 'providers' ? 'default' : 'outline'}
            onClick={() => setActiveTab('providers')}
            className="h-9"
          >
            Proveedores
          </Button>
        </div>

        {stats && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {statCards.map((item) => (
              <Card key={item.label} className="shadow-sm border">
                <CardHeader className="space-y-1 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <item.icon className="h-4 w-4 text-muted-foreground" />
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {item.label}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="px-3 pb-3 pt-2">
                  <p className="text-2xl font-semibold text-foreground">{item.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-md">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar por nombre, email o teléfono"
                className="h-9 rounded-lg border-input bg-background pl-8 text-sm"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value })}>
                <SelectTrigger className="h-9 w-[190px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="active">Activa</SelectItem>
                  <SelectItem value="suspended">Suspendida por deuda</SelectItem>
                  <SelectItem value="follow_up">En seguimiento</SelectItem>
                  <SelectItem value="blocked">Bloqueada</SelectItem>
                  <SelectItem value="closed">Cerrada</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2 text-sm">
                <span>Sin mov. (dias)</span>
                <Input
                  type="number"
                  min={0}
                  value={filters.noMovementDays}
                  onChange={(event) => setFilters({ ...filters, noMovementDays: event.target.value })}
                  className="h-9 w-24"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.hasDebt}
                onChange={(event) => setFilters({ ...filters, hasDebt: event.target.checked })}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              Con deuda
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.overdueDebt}
                onChange={(event) => setFilters({ ...filters, overdueDebt: event.target.checked })}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              Deuda vencida
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.overLimit}
                onChange={(event) => setFilters({ ...filters, overLimit: event.target.checked })}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              Supera límite
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.creditBalance}
                onChange={(event) => setFilters({ ...filters, creditBalance: event.target.checked })}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              Saldo a favor
            </label>
          </div>
        </div>

      </section>

      <section className="mt-4 space-y-4">
        {filteredAccounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed py-16 text-center text-muted-foreground">
            <CreditCardIcon className="h-10 w-10 text-muted-foreground/60" />
            <p>
              {searchTerm
                ? 'No encontramos resultados para la búsqueda actual'
                : 'No hay cuentas corrientes registradas'}
            </p>
          </div>
        ) : (
          <>
          <div className="hidden md:block rounded-xl border bg-card shadow-sm overflow-hidden">
            <Table className="w-full">
              <TableHeader>
                <TableRow className="border-b hover:bg-muted/50">
                  <TableHead className="text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-left">
                    {activeTab === 'clients' ? 'Cliente' : 'Proveedor'}
                  </TableHead>
                  <TableHead className="text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                  <TableHead className="text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-left">Últ. mov.</TableHead>
                  <TableHead className="hidden 2xl:table-cell text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-left">Últ. compra</TableHead>
                  <TableHead className="hidden 2xl:table-cell text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-left">Últ. pago</TableHead>
                  <TableHead className="text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-left">Límite</TableHead>
                  <TableHead className="hidden 2xl:table-cell text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-left">Disponible</TableHead>
                  <TableHead className="text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-left">Balance</TableHead>
                  <TableHead className="text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-left">Antig.</TableHead>
                  <TableHead className="text-foreground h-8 px-3 align-middle font-medium whitespace-nowrap text-right">
                    <span className="sr-only">Acciones</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedAccounts.map((account) => {
                  const available = Math.max(0, account.creditLimit - account.currentBalance);
                  return (
                    <TableRow key={account.id} className="border-b last:border-0 hover:bg-muted/50 cursor-pointer" onClick={() => navigate(`/current-accounts/${account.id}`)}>
                      <TableCell className="py-2 px-3 align-middle text-left max-w-[180px] cq-clamp-wide">
                        <div className="flex items-center gap-2 min-w-0">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="font-semibold text-foreground truncate cursor-default">{getDisplayName(account)}</div>
                            </TooltipTrigger>
                            <TooltipContent side="top">{getDisplayName(account)}</TooltipContent>
                          </Tooltip>
                          {account.linkedEntityId && (
                            <Tip label={account.entityType === 'client'
                              ? `También es proveedor${account.linkedName ? `: ${account.linkedName}` : ''}`
                              : `También es cliente${account.linkedName ? `: ${account.linkedName}` : ''}`}>
                              <span className="inline-flex items-center gap-1 shrink-0 text-[11px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-400">
                                ⇄ {account.entityType === 'client' ? 'Proveedor' : 'Cliente'}
                              </span>
                            </Tip>
                          )}
                        </div>
                        {account.entityType === 'provider' && (
                          <p className="text-xs text-muted-foreground">Proveedor</p>
                        )}
                      </TableCell>
                      <TableCell className="py-2 px-3 align-middle whitespace-nowrap text-left">
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`inline-flex h-2.5 w-2.5 rounded-full ${getStatusBadgeClass(account.status)}`} />
                          <span className="font-medium text-foreground">{getStatusLabel(account.status)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-2 px-3 align-middle whitespace-nowrap text-left">
                        <span className="text-sm">{formatDate(account.lastMovementAt)}</span>
                      </TableCell>
                      <TableCell className="hidden 2xl:table-cell py-2 px-3 align-middle whitespace-nowrap text-left">
                        <span className="text-sm">{formatDate(account.lastChargeAt)}</span>
                      </TableCell>
                      <TableCell className="hidden 2xl:table-cell py-2 px-3 align-middle whitespace-nowrap text-left">
                        <span className="text-sm">{formatDate(account.lastPaymentAt)}</span>
                      </TableCell>
                      <TableCell className="py-2 px-3 align-middle whitespace-nowrap text-left">
                        <p className="text-foreground">{formatCurrency(account.creditLimit)}</p>
                      </TableCell>
                      <TableCell className="hidden 2xl:table-cell py-2 px-3 align-middle whitespace-nowrap text-left">
                        <p className="text-primary">{formatCurrency(available)}</p>
                      </TableCell>
                      <TableCell className="py-2 px-3 align-middle whitespace-nowrap text-left">
                        {account.linkedEntityId && Number(account.linkedBalance || 0) > 0.009 ? (
                          <>
                            {/* Vinculado con deuda del otro lado: mostrar el neto en negativo */}
                            <p className="text-orange-500 font-medium">
                              {formatCurrency(Number(account.currentBalance || 0) - Number(account.linkedBalance || 0))}
                            </p>
                            <p className="text-xs text-orange-500/80">
                              {account.entityType === 'client' ? 'le debés como proveedor' : 'te debe como cliente'}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className={`${getBalanceColor(account.currentBalance, account.creditLimit)}`}>
                              {formatCurrency(account.currentBalance)}
                            </p>
                            {account.currentBalance > account.creditLimit && account.creditLimit > 0 && (
                              <p className="text-xs text-destructive">Sobre límite</p>
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell className="py-2 px-3 align-middle whitespace-nowrap text-left">
                        <Badge className={getAgeBadgeClass(account.debtAgeDays)}>
                          {getAgeLabel(account.debtAgeDays)}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-2 px-3 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          <Tip label="Ver detalles">
                            <Button
                              variant="outline"
                              size="icon"
                              className="size-8"
                              onClick={() => openAccountDetails(account)}
                              aria-label="Ver detalles"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Button>
                          </Tip>
                          <Tip label="Descargar movimientos PDF">
                            <Button
                              variant="outline"
                              size="icon"
                              className="size-8"
                              onClick={() => generateAccountPDF(account)}
                              aria-label="Descargar movimientos PDF"
                            >
                              <DocumentArrowDownIcon className="h-4 w-4" />
                            </Button>
                          </Tip>
                          <Tip label="Registrar pago">
                            <Button
                              variant="outline"
                              size="icon"
                              className="size-8"
                              onClick={() => navigate(`/current-accounts/pay?accountId=${account.id}`)}
                              aria-label="Registrar pago"
                            >
                              <BanknotesIcon className="h-4 w-4" />
                            </Button>
                          </Tip>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="md:hidden space-y-3">
            {paginatedAccounts.map((account) => {
              const available = Math.max(0, account.creditLimit - account.currentBalance);
              return (
                <div key={account.id} className="rounded-xl border bg-card/80 p-4 shadow-sm space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">{account.entityType === 'provider' ? 'Proveedor' : 'Cliente'}</p>
                      <p className="text-base font-semibold text-foreground">{getDisplayName(account)}</p>
                      {account.email && <p className="text-xs text-muted-foreground break-all">{account.email}</p>}
                      {account.phone && <p className="text-xs text-muted-foreground">{account.phone}</p>}
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className={`inline-flex h-2.5 w-2.5 rounded-full ${getStatusBadgeClass(account.status)}`} />
                      <span className="font-medium text-foreground">{getStatusLabel(account.status)}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge className={getAgeBadgeClass(account.debtAgeDays)}>
                      Antigüedad: {getAgeLabel(account.debtAgeDays)}
                    </Badge>
                    {account.lastMovementAt && (
                      <Badge variant="outline">Último mov.: {formatDate(account.lastMovementAt)}</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">Última compra</p>
                      <p className="font-semibold text-foreground">{formatDate(account.lastChargeAt)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">Último pago</p>
                      <p className="font-semibold text-foreground">{formatDate(account.lastPaymentAt)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">Límite</p>
                      <p className="font-semibold text-foreground">{formatCurrency(account.creditLimit)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">Disponible</p>
                      <p className="font-semibold text-primary">{formatCurrency(available)}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs uppercase text-muted-foreground">Balance actual</p>
                      <p className={`font-semibold ${getBalanceColor(account.currentBalance, account.creditLimit)}`}>
                        {formatCurrency(account.currentBalance)}
                      </p>
                      {account.currentBalance > account.creditLimit && account.creditLimit > 0 && (
                        <p className="text-xs text-destructive">Sobre límite</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 min-w-[100px] justify-center"
                      onClick={() => openAccountDetails(account)}
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      Ver detalles
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 min-w-[100px] justify-center"
                      onClick={() => generateAccountPDF(account)}
                    >
                      <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 min-w-[100px] justify-center"
                      onClick={() => navigate(`/current-accounts/pay?accountId=${account.id}`)}
                    >
                      <BanknotesIcon className="h-4 w-4 mr-1" />
                      Registrar pago
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </section>

      {!loading && filteredAccounts.length > 0 && (
        <div className="flex flex-col gap-3 px-2 md:flex-row md:items-center md:justify-between">
          <div className="flex-1 text-center text-sm text-muted-foreground md:text-left">
            Mostrando {startResult} de {totalResults} resultados
          </div>
          <div className="flex items-center justify-center space-x-6 lg:space-x-8">
            <div className="flex w-[100px] items-center justify-center text-sm font-medium">
              Página {currentPage} de {totalPages}
            </div>
          </div>
          <div className="flex items-center justify-center space-x-2 md:justify-end">
            <Button
              variant="outline"
              size="icon"
              onClick={goToFirstPage}
              disabled={currentPage === 1}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
            >
              <span className="sr-only">Ir a la primera página</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
              >
                <path d="m11 17-5-5 5-5"></path>
                <path d="m18 17-5-5 5-5"></path>
              </svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
            >
              <span className="sr-only">Ir a la página anterior</span>
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
            >
              <span className="sr-only">Ir a la página siguiente</span>
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToLastPage}
              disabled={currentPage === totalPages}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
            >
              <span className="sr-only">Ir a la última página</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
              >
                <path d="m6 17 5-5-5-5"></path>
                <path d="m13 17 5-5-5-5"></path>
              </svg>
            </Button>
          </div>
        </div>
      )}

      <Dialog
        open={showPaymentModal}
        onOpenChange={(open) => {
          if (!open) {
            closePaymentModal();
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Registrar pago</DialogTitle>
            <DialogDescription>
              {selectedAccount ? `Aplicar pago a ${getDisplayName(selectedAccount)}` : 'Selecciona la cuenta y completa los datos del pago.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitPayment(handleAddPayment)} className="space-y-4">
            {!selectedAccount && (
              <div className="space-y-2">
                <Label>Cuenta corriente *</Label>
                <Input
                  placeholder="Buscar por nombre o email..."
                  value={accountSearchTerm}
                  onChange={(event) => setAccountSearchTerm(event.target.value)}
                />
                <div className="max-h-48 overflow-y-auto rounded-lg border border-muted">
                  <div className="divide-y">
                    {filteredAccountOptions.map((account) => (
                      <button
                        type="button"
                        key={account.id}
                        className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-muted/70"
                        onClick={() => {
                          setSelectedAccount(account);
                          setAccountSearchTerm(getDisplayName(account));
                        }}
                      >
                        <span className="text-sm font-medium text-foreground">{getDisplayName(account)}</span>
                        <span className="text-xs text-muted-foreground">
                          {account.email || account.phone || 'Sin datos de contacto'}
                        </span>
                      </button>
                    ))}
                    {filteredAccountOptions.length === 0 && (
                      <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                        No se encontraron cuentas
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {selectedAccount && (
              <button
                type="button"
                className="w-full rounded-lg border border-dashed px-3 py-2 text-left text-sm text-muted-foreground transition hover:border-muted-foreground hover:text-foreground"
                onClick={() => {
                  setSelectedAccount(null);
                  setAccountSearchTerm('');
                }}
              >
                Registrando pago para{' '}
                <span className="font-semibold text-foreground">{getDisplayName(selectedAccount)}</span>
                <span className="block text-xs text-muted-foreground/80">Toca para elegir otro</span>
              </button>
            )}

            <div className="space-y-1">
              <Label htmlFor="payment-amount">Monto *</Label>
              <Input
                id="payment-amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                {...registerPayment('amount', { required: 'Monto es requerido', min: { value: 0.01, message: 'Debe ser mayor a 0' } })}
              />
              {paymentErrors.amount && <p className="text-sm text-destructive">{paymentErrors.amount.message}</p>}
            </div>

            <div className="space-y-1">
              <Label>Método de pago *</Label>
              <Controller
                control={paymentControl}
                name="paymentMethod"
                rules={{ required: 'Método de pago es requerido' }}
                render={({ field }) => (
                  <Select
                    value={field.value || ''}
                    onValueChange={(value) => {
                      field.onChange(value);
                      setSelectedPaymentMethod(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentMethodOptions.map((m) => (
                        <SelectItem key={m.code} value={m.code}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {paymentErrors.paymentMethod && (
                <p className="text-sm text-destructive">{paymentErrors.paymentMethod.message}</p>
              )}
            </div>

            {selectedPaymentMethod === 'transferencia' && (
              <div className="space-y-1">
                <Label>Banco destino *</Label>
                <Controller
                  control={paymentControl}
                  name="bank"
                  rules={{ required: 'Banco es requerido para transferencia' }}
                  render={({ field }) => (
                    <BankCombobox value={field.value || ''} onChange={field.onChange} placeholder="Seleccionar banco..." />
                  )}
                />
                {paymentErrors.bank && <p className="text-sm text-destructive">{paymentErrors.bank.message}</p>}
              </div>
            )}

            {selectedPaymentMethod === 'cheque' && (
              <>
                <div className="space-y-1">
                  <Label htmlFor="check-number">Número de cheque *</Label>
                  <Input
                    id="check-number"
                    type="text"
                    placeholder="Número del cheque"
                    {...registerPayment('checkNumber', {
                      required: 'Número de cheque es requerido'
                    })}
                  />
                  {paymentErrors.checkNumber && (
                    <p className="text-sm text-destructive">{paymentErrors.checkNumber.message}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label>Banco del cheque *</Label>
                  <Controller
                    control={paymentControl}
                    name="checkBank"
                    rules={{ required: 'Banco del cheque es requerido' }}
                    render={({ field }) => (
                      <BankCombobox value={field.value || ''} onChange={field.onChange} placeholder="Seleccionar banco..." />
                    )}
                  />
                  {paymentErrors.checkBank && (
                    <p className="text-sm text-destructive">{paymentErrors.checkBank.message}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="check-date">Fecha de cobro *</Label>
                  <Controller
                    name="checkDate"
                    control={paymentControl}
                    rules={{ required: 'Fecha de cobro es requerida' }}
                    render={({ field }) => (
                      <DatePicker
                        id="check-date"
                        value={field.value || ''}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  {paymentErrors.checkDate && (
                    <p className="text-sm text-destructive">{paymentErrors.checkDate.message}</p>
                  )}
                </div>
              </>
            )}

            <div className="space-y-1">
              <Label htmlFor="payment-description">Descripción</Label>
              <Textarea
                id="payment-description"
                placeholder="Detalle del pago"
                rows={2}
                {...registerPayment('description')}
              />
            </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={closePaymentModal}>
            Cancelar
          </Button>
          <Button type="submit">Registrar pago</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>

  <Dialog
    open={showCreditLimitModal && !!selectedAccount}
        onOpenChange={(open) => {
          if (!open) {
            closeCreditLimitModal();
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Cambiar límite de crédito</DialogTitle>
            <DialogDescription>
              {selectedAccount ? `Cuenta: ${getDisplayName(selectedAccount)}` : 'Actualiza el límite disponible.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitCredit(handleUpdateCreditLimit)} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="current-limit">Límite actual</Label>
              <Input
                id="current-limit"
                type="text"
                value={selectedAccount ? formatCurrency(selectedAccount.creditLimit) : ''}
                disabled
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="new-limit">Nuevo límite *</Label>
              <Input
                id="new-limit"
                type="number"
                step="0.01"
                placeholder="0.00"
                {...registerCredit('creditLimit', { required: 'Límite es requerido', min: { value: 0, message: 'Debe ser positivo' } })}
              />
              {creditErrors.creditLimit && (
                <p className="text-sm text-destructive">{creditErrors.creditLimit.message}</p>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={closeCreditLimitModal}>
                Cancelar
              </Button>
              <Button type="submit">Actualizar límite</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Saldo inicial (migración del sistema anterior) */}
      <Dialog open={showOpeningModal} onOpenChange={setShowOpeningModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cargar saldo inicial</DialogTitle>
            <DialogDescription>
              {activeTab === 'clients'
                ? 'Deuda que el cliente arrastra del sistema anterior (lo que te debe).'
                : 'Saldo que arrastrás con el proveedor del sistema anterior (lo que le debés).'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{activeTab === 'clients' ? 'Cliente' : 'Proveedor'} *</Label>
              {activeTab === 'clients' ? (
                <ClientCombobox
                  clients={openingEntities}
                  selectedClient={openingSelected}
                  onSelect={(c) => setOpeningSelected(c)}
                  placeholder="Buscar cliente..."
                  getClientName={(c: any) => c.companyName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Sin nombre'}
                />
              ) : (
                <ProviderCombobox
                  providers={openingEntities}
                  selectedProvider={openingSelected}
                  onSelect={(p) => setOpeningSelected(p)}
                  placeholder="Buscar proveedor..."
                />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="opening-amount">Monto del saldo *</Label>
              <Input
                id="opening-amount"
                type="number"
                step="0.01"
                min="0"
                value={openingAmount}
                onChange={(e) => setOpeningAmount(e.target.value)}
                placeholder="0.00"
              />
              {openingAmount !== '' && !Number.isNaN(Number(openingAmount)) && (
                <p className="text-sm font-medium text-foreground">= {formatCurrency(Number(openingAmount))}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Escribí el número sin separador de miles (ej: 977276, no 977.276 — el punto es decimal). Si lo recargás, reemplaza el saldo anterior (no se suma).
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="opening-date">Fecha (opcional)</Label>
              <DatePicker
                id="opening-date"
                value={openingDate}
                onChange={setOpeningDate}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="opening-note">Referencia / comentario (opcional)</Label>
              <Input
                id="opening-note"
                type="text"
                value={openingNote}
                onChange={(e) => setOpeningNote(e.target.value)}
                placeholder="Ej: facturas de junio, remito 1234, cheques pendientes..."
              />
              <p className="text-xs text-muted-foreground">Queda anotado en el movimiento de saldo inicial para saber de qué es la deuda.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setShowOpeningModal(false)}>Cancelar</Button>
            <Button type="button" onClick={saveOpeningBalance} disabled={openingSaving}>
              {openingSaving ? 'Guardando...' : 'Cargar saldo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CurrentAccounts;


